import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllNotifications, NotificationValue } from '@utils/sharedUtils';
import { useAppSelector } from '@store/hooks';
import { HOSPITAL } from '@constants/constants';
import { updateNotificationApi } from '@utils/notificationUtils';
import Swal from 'sweetalert2';

const NotificationList = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [notifications, setNotifications] = useState<NotificationValue[]>([]);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [commandInputs, setCommandInputs] = useState<Record<string, string>>({});
    const [submittingCommandId, setSubmittingCommandId] = useState<string | null>(null);

    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole || ''
    );
    const accessToken = useAppSelector(
        (state) => state.user.accessToken
    );

    const fetchNotifications = async (loadMore = false) => {
        try {
            const key = loadMore ? lastEvaluatedKey : undefined;
            const { notifications: fetchedNotifications, lastEvaluatedKey: newKey } = await getAllNotifications(key);
            setNotifications(prevNotifications => loadMore ? [...prevNotifications, ...fetchedNotifications] : fetchedNotifications);
            setLastEvaluatedKey(newKey);
            setHasMore(!!newKey);
        } catch (err) {
            console.error("Failed to load notifications", err);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const filteredUsers = useMemo(() => {
        let filtered = notifications;
        if (searchTerm) {
            filtered = filtered.filter((notification) =>
                notification.message
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            );
        }
        return filtered;
    }, [searchTerm, notifications]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];
        sorted.sort((a, b) => {
            const dateA = new Date(a.createdAtRaw || 0);
            const dateB = new Date(b.createdAtRaw || 0);
            if (sortField === 'oldest') {
                return dateA.getTime() - dateB.getTime();
            }
            return dateB.getTime() - dateA.getTime();
        });
        return sorted;
    }, [filteredUsers, sortField]);

    const handleLoadMore = () => {
        fetchNotifications(true);
    };

    const handleCommandInputChange = (id: string, text: string) => {
        setCommandInputs(prev => ({ ...prev, [id]: text }));
    };

    const handleHospitalSubmitCommand = async (notificationUuid: string, deviceId: string) => {
        const text = commandInputs[notificationUuid]?.trim();
        if (!text) return;
        setSubmittingCommandId(notificationUuid);
        try {
            await updateNotificationApi(
                {
                    uuid: notificationUuid,
                    device_id: deviceId,
                    command: text,
                },
                accessToken
            );
            Swal.fire({
                position: 'top-end',
                icon: 'success',
                title: 'Command submitted successfully!',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
            });
            setNotifications(prev =>
                prev.map(item => (item.id === notificationUuid ? { ...item, command: text } : item))
            );
            setCommandInputs(prev => ({ ...prev, [notificationUuid]: '' }));
        } catch (err: any) {
            console.error('Failed to submit command:', err);
            Swal.fire({
                position: 'top-end',
                icon: 'error',
                title: err.message || 'Failed to submit command',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
            });
        } finally {
            setSubmittingCommandId(null);
        }
    };

    return (
        <div className="w-full">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="text-lg font-semibold text-slate-900">
                    {t('notification.title')}
                </h1>
            </div>

            <div className="flex flex-col gap-4 max-w-3xl mx-auto py-2">
                {sortedData.length === 0 ? (
                    <div className="bg-white p-8 text-center rounded-2xl shadow-sm text-slate-500 font-medium">
                        {t('notification.no_data') || 'No notifications found.'}
                    </div>
                ) : (
                    sortedData.map((item) => {
                        const activeCommand = item.command || '';

                        return (
                            <div key={item.id} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex flex-col gap-3 transition hover:shadow-md">
                                <div className="flex flex-col gap-0.5">
                                    <h3 className="text-base font-bold text-slate-900 leading-snug">
                                        {item.deviceId ? `Error in ${item.deviceId}` : (item.message || 'Notification')}
                                    </h3>
                                    <p className="text-sm font-semibold text-slate-700">
                                        {item.errorCode ? item.errorCode : item.message}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 pt-2 border-t border-slate-100">
                                    <div>
                                        <span className="text-slate-400">Device ID:</span> <span className="font-semibold text-slate-800">{item.deviceId || '--'}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-400">Device Name:</span> <span className="font-semibold text-slate-800">{item.deviceName || '--'}</span>
                                    </div>
                                    {item.errorCode && (
                                        <div className="col-span-2">
                                            <span className="text-slate-400">Error Code:</span> <span className="font-semibold text-slate-800">{item.errorCode}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Nurse summary details if present */}
                                {item.nurseSummary && (
                                    <div className="bg-slate-50/80 rounded-xl p-3.5 border border-slate-200/80 flex flex-col gap-2.5 mt-1 w-full overflow-hidden">
                                        <div className="flex flex-col gap-1 w-full">
                                            <p className="text-xs font-bold text-slate-900">Nurse summary:</p>
                                            <div className="text-xs text-slate-700 leading-relaxed font-medium bg-white p-3 rounded-lg border border-slate-200/80 break-words whitespace-pre-wrap max-w-full overflow-hidden">
                                                {item.nurseSummary}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1 text-xs pt-1 border-t border-slate-200/60">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-bold text-slate-900">Error Occurred Time :</span>
                                                <span className="text-slate-700">{item.errorOccurredTime || item.notificationDate || '--'}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-bold text-slate-900">Error Resolved Time :</span>
                                                <span className="text-slate-700">{item.errorResolvedTime || '--'}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-bold text-slate-900">Resolved Nurse Name:</span>
                                                <span className="text-slate-800 font-semibold">{item.resolvedNurseName || '--'}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Command display if present */}
                                {activeCommand && (
                                    <div className="flex items-start gap-1.5 text-xs bg-blue-50/60 p-3 rounded-xl border border-blue-100 text-blue-900 mt-1">
                                        <span className="font-bold shrink-0">Command:</span>
                                        <span className="font-medium">{activeCommand}</span>
                                    </div>
                                )}

                                {/* Optional Command Input for Hospital Account */}
                                {loggedInUserRole.toLowerCase() === HOSPITAL.toLowerCase() && (
                                    <div className="mt-2 pt-3 border-t border-slate-100 flex flex-col gap-2">
                                        <label className="text-xs font-bold text-slate-900">
                                            Add Command (Optional) :
                                        </label>
                                        <textarea
                                            className="w-full p-3 text-xs border border-blue-400/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white placeholder-slate-400 resize-none"
                                            rows={2}
                                            placeholder="Enter your command ...."
                                            value={commandInputs[item.id] || ''}
                                            onChange={(e) => handleCommandInputChange(item.id, e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl transition shadow-md shadow-blue-600/20 disabled:opacity-50"
                                            onClick={() => handleHospitalSubmitCommand(item.id, item.deviceId)}
                                            disabled={submittingCommandId === item.id || !commandInputs[item.id]?.trim()}
                                        >
                                            {submittingCommandId === item.id ? 'Submitting...' : 'Submit'}
                                        </button>
                                    </div>
                                )}

                                <div className="text-right text-[11px] font-medium text-slate-400 mt-1">
                                    {item.notificationDate}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {hasMore && (
                <div className="text-center mt-4">
                    <button onClick={handleLoadMore} className="btn btn-primary">
                        {t('load_more')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationList;
